import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Info, Users } from 'lucide-react';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchManagerInboxRequest, fetchSkillEvalsByUserRequest } from '../../../redux/actions/skillEvaluationActions';
import SkillGapTable from '../components/SkillGapTable';
import { buildRadarFilterOptions, filterRadarData, filterUsersByRadarFilters, RadarFiltersPanel, toRadarSkill } from '../radarFilters';
import '../EvaluationDesign.css';

export default function GapPage() {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth || {});
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { inboxManager, skillEvaluations } = useSelector(state => state.skillEvaluation || { inboxManager: [], skillEvaluations: [], loading: false });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [gapFilters, setGapFilters] = useState({
        entity: '',
        poste: '',
        domaine: '',
        theme: '',
        competence: '',
    });

    const isManagerOrRH = useMemo(() => {
        const role = (user?.role?.name || user?.role || "").toUpperCase();
        return role.includes("MANAGER") || role.includes("RH") || role.includes("ADMIN");
    }, [user]);

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        if (isManagerOrRH) {
            dispatch(fetchManagerInboxRequest());
        } else if (user?.id || user?.uid) {
            dispatch(fetchSkillEvalsByUserRequest(user.id || user.uid));
        }
    }, [dispatch, isManagerOrRH, user]);

    const team = useMemo(() => {
        return (inboxManager || []).map(e => ({
            ...e,
            initiales: e.userFullName ? e.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName,
            color: '#204385'
        }));
    }, [inboxManager]);

    const ownGapSource = useMemo(() => [{ niveaux: skillEvaluations || [] }], [skillEvaluations]);
    const gapFilterOptions = useMemo(
        () => buildRadarFilterOptions(isManagerOrRH ? team : ownGapSource),
        [isManagerOrRH, team, ownGapSource]
    );

    const filteredTeam = useMemo(() =>
        filterUsersByRadarFilters(team, { ...gapFilters, search: searchQuery }).filter(u => {
            const matchCampagne = !selectedCampagneId || u.campagneId === parseInt(selectedCampagneId);
            return matchCampagne;
        }),
        [team, gapFilters, searchQuery, selectedCampagneId]
    );

    const activeCampagnes = useMemo(() => 
        (campagnesList || []).filter(c => c.status === true), 
    [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    const gapData = useMemo(() => {
        let evals = [];
        if (isManagerOrRH) {
            if (!selectedUser) return [];
            evals = [selectedUser]; 
        } else {
            evals = skillEvaluations;
        }

        const found = evals.find(e => !selectedCampagneId || e.campagneId === parseInt(selectedCampagneId));
        if (!found || !found.niveaux) return [];

        return filterRadarData(found.niveaux.map(n => toRadarSkill(n)), gapFilters);
    }, [isManagerOrRH, selectedUser, skillEvaluations, selectedCampagneId, gapFilters]);

    return (
        <div className="eval-page">
            <div className="eval-header">
                <div>
                    <h1>Analyse des écarts</h1>
                    <p>Comparez vos compétences aux requis du poste sous forme de tableau.</p>
                </div>
            </div>

            <RadarFiltersPanel
                filters={gapFilters}
                setFilters={setGapFilters}
                options={gapFilterOptions}
                showUserFilters={false}
                campaignValue={selectedCampagneId}
                onCampaignChange={setSelectedCampagneId}
                campaignOptions={activeCampagnes}
                campaignPlaceholder="Selectionnez une campagne"
            />
            <div className="eval-layout">
                {isManagerOrRH && (
                    <aside className="eval-sidebar">
                        <div className="eval-toolbar">
                            <div className="eval-search-box">
                                <Search className="eval-search-icon" size={16} />
                                <input 
                                    type="text" 
                                    className="eval-input" 
                                    placeholder="Chercher collaborateur..." 
                                    value={searchQuery} 
                                    onChange={(e) => setSearchQuery(e.target.value)} 
                                />
                            </div>
                        </div>
                        <div className="eval-list">
                            {filteredTeam.map(u => (
                                <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                    <div className="eval-avatar" style={{ backgroundColor: u.color }}>{u.initiales}</div>
                                    <div className="eval-user-info">
                                        <div className="eval-user-name">{u.nom}</div>
                                        <div className="eval-user-sub">{u.campagneNom}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </aside>
                )}

                <main className="eval-main">
                    {isManagerOrRH && !selectedUser ? (
                        <div className="eval-empty">
                            <Users size={64} className="eval-empty-icon" />
                            <h3>Suivi des Écarts</h3>
                            <p>Sélectionnez un membre de votre équipe à gauche pour voir ses scores détaillés.</p>
                        </div>
                    ) : gapData.length > 0 ? (
                        <div className="eval-content">
                            <div className="eval-card">
                                {/*<div className="eval-section-title">*/}
                                {/*    <Info size={18} /> Tableau des écarts - {isManagerOrRH ? selectedUser.nom : user.firstName + ' ' + user.lastName}*/}
                                {/*</div>*/}
                                <SkillGapTable data={gapData} />
                                {/*<div style={{ marginTop: '24px', fontSize: '13px', color: '#64748b' }}>*/}
                                {/*    Ce tableau permet d'identifier rapidement les compétences à renforcer (écart négatif) ou les expertises maîtrisées.*/}
                                {/*</div>*/}
                            </div>
                        </div>
                    ) : (
                        <div className="eval-empty">
                            <Info size={64} className="eval-empty-icon" />
                            <h3>Aucune donnée</h3>
                            <p>Aucune évaluation validée n'a été trouvée pour cette campagne.</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
